import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of, delay } from 'rxjs';
import { environment } from '../../../environments/environment';

let mockFlagEnabled = true;

function buildJwt(email: string, groups: string[]): string {
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const payload = btoa(
    JSON.stringify({
      sub: 'mock-user-id',
      email,
      'cognito:groups': groups,
      exp: Math.floor(Date.now() / 1000) + 3600,
      iat: Math.floor(Date.now() / 1000),
    })
  );
  return `${header}.${payload}.mock-signature`;
}

function buildAuthResult(email: string) {
  const token = buildJwt(email, ['sc-admin']);
  return {
    AuthenticationResult: {
      AccessToken: token,
      IdToken: token,
      RefreshToken: 'mock-refresh-token',
      ExpiresIn: 3600,
      TokenType: 'Bearer',
    },
  };
}

function buildFlagStatus() {
  return {
    enabled: mockFlagEnabled,
    lastModifiedBy: 'admin@spin.com',
    lastModifiedAt: new Date(Date.now() - 86400000).toISOString(),
    reason: 'Activación programada para terminales de producción',
    effectiveAt: new Date(Date.now() - 86400000).toISOString(),
  };
}

function buildAuditResponse() {
  const entries = [
    {
      timestamp: new Date(Date.now() - 86400000).toISOString(),
      user: 'admin@spin.com',
      action: 'UPDATE',
      previousValue: false,
      newValue: true,
      reason: 'Activación programada para terminales de producción',
      effectiveAt: new Date(Date.now() - 86400000).toISOString(),
      sourceIp: '10.0.1.50',
      userAgent: 'Mozilla/5.0',
    },
    {
      timestamp: new Date(Date.now() - 172800000).toISOString(),
      user: 'carlos.ivan@spin.com',
      action: 'UPDATE',
      previousValue: true,
      newValue: false,
      reason: 'Desactivación temporal por mantenimiento en terminales',
      effectiveAt: new Date(Date.now() - 172800000).toISOString(),
      sourceIp: '10.0.1.51',
      userAgent: 'Mozilla/5.0',
    },
    {
      timestamp: new Date(Date.now() - 604800000).toISOString(),
      user: 'edson@spin.com',
      action: 'UPDATE',
      previousValue: false,
      newValue: true,
      reason: 'Prueba de encriptación DUKPT en ambiente de desarrollo',
      effectiveAt: new Date(Date.now() - 604800000).toISOString(),
      sourceIp: '10.0.1.52',
      userAgent: 'Mozilla/5.0',
    },
    {
      timestamp: new Date(Date.now() - 1209600000).toISOString(),
      user: 'luis.fernando@spin.com',
      action: 'UPDATE',
      previousValue: true,
      newValue: false,
      reason: 'Rollback de configuración por incidencia en POS',
      effectiveAt: new Date(Date.now() - 1209600000).toISOString(),
      sourceIp: '10.0.1.53',
      userAgent: 'Mozilla/5.0',
    },
    {
      timestamp: new Date(Date.now() - 2592000000).toISOString(),
      user: 'admin@spin.com',
      action: 'UPDATE',
      previousValue: false,
      newValue: true,
      reason: 'Configuración inicial de encriptación DUKPT',
      effectiveAt: new Date(Date.now() - 2592000000).toISOString(),
      sourceIp: '10.0.1.50',
      userAgent: 'Mozilla/5.0',
    },
  ];
  return { items: entries, cursor: null };
}

export const mockInterceptor: HttpInterceptorFn = (req, next) => {
  if (!('mock' in environment) || !(environment as Record<string, unknown>)['mock']) {
    return next(req);
  }

  const mockDelay = 400;

  // Cognito auth calls
  if (req.url.includes('cognito-idp') && req.method === 'POST') {
    const body = req.body as Record<string, unknown>;
    const email =
      (body?.['AuthParameters'] as Record<string, string>)?.['USERNAME'] ?? 'admin@spin.com';
    return of(new HttpResponse({ status: 200, body: buildAuthResult(email) })).pipe(
      delay(mockDelay)
    );
  }

  // GET /flag
  if (req.url.includes('/flag') && req.method === 'GET') {
    return of(new HttpResponse({ status: 200, body: buildFlagStatus() })).pipe(delay(mockDelay));
  }

  // PUT /flag
  if (req.url.includes('/flag') && req.method === 'PUT') {
    const body = req.body as Record<string, unknown>;
    mockFlagEnabled = body['enabled'] as boolean;
    const response = {
      message: 'Flag updated successfully',
      enabled: mockFlagEnabled,
      effectiveAt: new Date().toISOString(),
      modifiedBy: 'admin@spin.com',
      modifiedAt: new Date().toISOString(),
    };
    return of(new HttpResponse({ status: 200, body: response })).pipe(delay(800));
  }

  // GET /audit
  if (req.url.includes('/audit') && req.method === 'GET') {
    return of(new HttpResponse({ status: 200, body: buildAuditResponse() })).pipe(
      delay(mockDelay)
    );
  }

  return next(req);
};
